import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { REFRESH_AUDIO_INTERVAL } from "@/common/config";
import type { NoteTemplate } from "@/common/types/notesTemplates";
import { publicTraversalError } from "@/lib/public-pagination";
import { queryClient } from "@/lib/query-client";
import {
  contentKey,
  noteOptions,
  recordingOptions,
  useCompleteContent,
  type VisitRecording,
  type VisitVersion,
  versionOptions,
} from "@/lib/visit-content-queries";
import {
  isInaccessible,
  isInProgress,
  useVisitDetail,
  type VisitDetailRead,
} from "@/lib/visit-queries";

export interface VisitReads extends VisitDetailRead {
  notes: NoteTemplate[];
  multiRecordingFiles: VisitRecording[];
  relatedError: boolean;
  selectedVersion: string;
  setVersionId: (version: string) => void;
  fileVersion: VisitVersion | null;
  versionLoading: boolean;
  versionError: boolean;
}
function useOwnedVisitReads(id: string): VisitReads {
  const parent = useVisitDetail(id);
  const [versionPublicId, setVersionId] = useState("latest_version");
  const { scope, file } = parent;
  const queryScope = scope ?? { uid: "", accountPublicId: "", generation: 0 };
  const identity = JSON.stringify(contentKey(queryScope, id));
  const enabled = !!scope && parent.accepted;
  useEffect(() => {
    if (!enabled && scope) {
      const queryKey = [
        "visit-reads",
        scope.uid,
        scope.accountPublicId,
        scope.generation,
        "related",
        id,
      ];
      void queryClient.cancelQueries({ queryKey });
      queryClient.removeQueries({ queryKey });
      setVersionId("latest_version");
    }
  }, [enabled, scope, id]);
  // Notes and recordings are polled only while the visit or one of its
  // recordings is in progress (B5); a settled visit changes through actions
  // on this page, which refresh the reads themselves.
  const [recordingsBusy, setRecordingsBusy] = useState(false);
  const polling = isInProgress(file?.status) || recordingsBusy;
  const notes = useInfiniteQuery({
    ...noteOptions(queryScope, id),
    enabled,
    refetchInterval: (q) =>
      !polling ||
      q.state.status === "error" ||
      publicTraversalError(q.state.data?.pages)
        ? false
        : REFRESH_AUDIO_INTERVAL,
  });
  const recordings = useInfiniteQuery({
    ...recordingOptions(queryScope, id),
    enabled,
    refetchInterval: (q) =>
      !polling ||
      q.state.status === "error" ||
      publicTraversalError(q.state.data?.pages)
        ? false
        : REFRESH_AUDIO_INTERVAL,
  });
  const completeNotes = useCompleteContent(notes, enabled, identity);
  const completeRecordings = useCompleteContent(recordings, enabled, identity);
  const busy = completeRecordings.some((recording) =>
    isInProgress(recording.status),
  );
  useEffect(() => {
    setRecordingsBusy(busy);
  }, [busy]);
  // When the visit settles, read its notes and recordings once more, so the
  // last poll cannot miss what was written just before it settled.
  const wasPolling = useRef(polling);
  const { refetch: refetchNotes } = notes;
  const { refetch: refetchRecordings } = recordings;
  useEffect(() => {
    if (wasPolling.current && !polling && enabled) {
      void refetchNotes();
      void refetchRecordings();
    }
    wasPolling.current = polling;
  }, [polling, enabled, refetchNotes, refetchRecordings]);
  const selectedVersion = file?.versions.some(
    (version) => String(version.public_id) === versionPublicId,
  )
    ? versionPublicId
    : "latest_version";
  const version = useQuery({
    ...versionOptions(queryScope, id, selectedVersion),
    enabled: enabled && selectedVersion !== "latest_version",
  });
  const refresh = useCallback(async () => {
    if (!scope) return;
    await Promise.all([
      parent.refresh(),
      queryClient.resetQueries({ queryKey: contentKey(scope, id) }),
    ]);
  }, [scope, id, parent.refresh]);
  return {
    ...parent,
    refresh,
    notes: completeNotes,
    multiRecordingFiles: completeRecordings,
    relatedError:
      enabled &&
      (notes.isError ||
        recordings.isError ||
        publicTraversalError(notes.data?.pages) ||
        publicTraversalError(recordings.data?.pages)),
    selectedVersion,
    setVersionId,
    fileVersion:
      enabled &&
      selectedVersion !== "latest_version" &&
      !isInaccessible(version.error)
        ? (version.data ?? null)
        : null,
    versionLoading:
      enabled && selectedVersion !== "latest_version" && version.isPending,
    versionError:
      enabled && selectedVersion !== "latest_version" && version.isError,
  };
}
const VisitReadsContext = createContext<ReturnType<
  typeof useOwnedVisitReads
> | null>(null);
export function VisitReadsProvider({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}): JSX.Element {
  const value = useOwnedVisitReads(id);
  return (
    <VisitReadsContext.Provider value={value}>
      {children}
    </VisitReadsContext.Provider>
  );
}

// Supplies fixed visit reads without fetching: for Storybook previews of
// visit components with synthetic data.
export function StaticVisitReadsProvider({
  value,
  children,
}: {
  value: VisitReads;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <VisitReadsContext.Provider value={value}>
      {children}
    </VisitReadsContext.Provider>
  );
}

export function useVisitReads(): VisitReads {
  const context = useContext(VisitReadsContext);
  if (!context) throw new Error("Visit reads require an owned visit provider");
  return context;
}
